/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import React, { useMemo, useRef, useState } from 'react';
import { shallowEqual, useIntl, useSelector } from 'umi';
import { Text, Icon } from "tea-component";
import cx from "classnames";

import CardBox from '@/pages/dashboard/components/CardBox';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { formatNum, formatPercentNum, getIsErrorValue, splitDateTimeNoSpace } from '@/pages/dashboard/utils/utils';
import { addChartSymbol, contrastLine, DataZoom, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { lineFormatterFunc, yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import SvgIcon from '@/pages/home/components/SvgIcon';
import './index.scss';
import moment from 'moment';
import EventDetailsPop, { useEventDetailsPop } from '../../../../eventAnalysis/EventDetailsPop';
import DownloadOptionsModalCo from '../DownloadOptionsModalCo';
import { exportExcel } from '@/pages/dashboard/pages/realtime/_config/exportExcel';
import _ from 'lodash-es';
import { getImg } from '@/pages/home/_config/getImgs';
import { useLinkPath } from '@/pages/dashboard/utils/customHook';
import { useDateChangeStatus } from '../../hooks';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import CommonBubble from '@home/components/CommonBubble';
import { dynamicString } from '@home/utils/dynamicString';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { getLanguage } from '@home/utils/common';

const getCellValue = (_arr: { [x: string]: { [x: string]: { count: any; }; }; }, key: number, ind: number) => _arr[key]?.[ind]?.count ? _arr[key][ind]?.count : '';

const formatConfig = (str: string, allData: { [x: string]: any; }) =>{
  if (typeof str === 'string' && str.length > 0) {
    const matches = str.match(/{{(.*?)}}/);
    if (matches && matches.length > 1) {
      const {input} = matches;
      const [key, type, format, offset, unit] = matches[1].split('|');
      let value = allData?.[key];
      if (value) {
        switch (type) {
          case 'date':
            if (offset && unit) {
              // eslint-disable-next-line radix
              value = moment(value).add(parseInt(offset), unit).format(format);
              return input?.replace(matches[0], value) || '';
            }
            value = moment(value).format(format);
            return input?.replace(matches[0], value) || '';
          case 'string':
            return input?.replace(matches[0], value);
          default:
            return value;
        }
      }else{
        switch (type) {
          case 'string':
            return input?.replace(matches[0], '');
        }
      }
    }
  }
  return str;
};

const loopKeysAndValues = (obj: { [x: string]: any; }, prefix = '', allData: { [x: string]: any; }) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      if (key === 'cssStyle') {
        continue;
      }
      const value = obj[key];
      if (typeof value === 'object' && value !== null) {
        loopKeysAndValues(value, `${prefix}${key}.`, allData);
      } else {
        // eslint-disable-next-line no-param-reassign
        obj[key] = formatConfig(value, allData);
      }
    }
  }
  return obj;
};

const RealtimeRetentionCo = (props: any) => {
  const { formValue, data, config, event, eventData, bordered = true, height, timeZone: propsTimeZone} = props;
  const { locale, formatMessage } = useIntl();
  const isEN = locale === 'en-US';
  const { componentData, loading, errorMsg: error } = data || {};
  const count = componentData?.count || undefined;
  const popRef = useRef();
  const { currentEvents, slotConfigFunc } = useEventDetailsPop(eventData, popRef);
  const linkPath = useLinkPath( event?.link || "eventAnalysis/eventDetails");
  const { releaseTime, gameTimeZone } = useSelector((state: any) => ({
    releaseTime: state?.dashboardSetting?.gameConfig?.game_phase_timeline?.release_time,
    gameTimeZone: state?.dashboardSetting?.gameConfig?.time_zone || ''
  }), shallowEqual);
  const timeZone = propsTimeZone || gameTimeZone;
  const { isChangeDate, isReleaseDateChange } = useDateChangeStatus(formValue, releaseTime, timeZone, ) || {};

  const formatConfig = useMemo(()=>{
    const cloneConfig = _.cloneDeep(config);
    const lastUpdateTime = componentData?.line_dot?.[componentData?.line_dot?.length - 1]?.time_dot?.slice(11)?.slice(0, 5) || '';
    return loopKeysAndValues(cloneConfig, '', {...formValue, ...componentData, lastUpdateTime, timeZone});
  },[config, formValue, componentData, timeZone]);
  const { table, chart, subTitle, download, className, downloadOptions = { timeScale: 5, sort: 1 }, noDataDisplay = false } = formatConfig || {};
  const [showDownload, setShowDownload] = useState(false);

  // 模板字符串兼容
  const { isPercent, isMoney, isNoMoney, formatRule, title, titleIcon, explain, redExplain } = useMemo(() => {
    const { 
      formatRule: formatRuleStr, 
      title: titleEn, 
      titleCn, 
      formatTitle, 
      titleIcon = '',
      explain: explainEn,
      explainCn,
      redExplain: redExplainEn,
      redExplainCn,
    } = formatConfig || {};
    const { dataType } = config || {};
    const formatRule = dynamicString(formatRuleStr, data);
    const isPercent = dataType === 'percent' || formatRule?.toLowerCase()?.includes('percent');
    const isMoney = dataType === 'money';
    const isNoMoney = dataType !== 'money';
    const dynamicTitle = getLanguage(isEN, dynamicString(titleEn, data), dynamicString(titleCn, data));
    const title = titleEn && titleCn ? dynamicTitle : formatTitle ? formatMessage({id: formatTitle}) : '';
    const explain = getLanguage(isEN, dynamicString(explainEn, data), dynamicString(explainCn, data)) || '';
    const redExplain = getLanguage(isEN, dynamicString(redExplainEn, data), dynamicString(redExplainCn, data)) || '';
    
    return {
      isPercent,
      isMoney,
      isNoMoney,
      formatRule,
      title,
      titleIcon,
      explain,
      redExplain,
    };
  }, [config, data, isEN]);

  const { explainInfo } = useSelector((state: any) => ({
    explainInfo: state.dashboard?.explainInfo,
  }));
  const { rainbowKey, titleExplainEn, titleExplainCn } = formatConfig || {};
  const titleExplain = getExplainVal(explainInfo, rainbowKey, locale, titleExplainEn, titleExplainCn,);

  const titleCom = useMemo(()=>(
    <>
      {
        formatConfig?.preTitleIcon && (
          <img className='dbd-realtime-icon-pre-img' src={getImg(formatConfig?.preTitleIcon)} />
        )
      }
      <p>
        <CommonBubble content={titleExplain}>
          {title}
        </CommonBubble>
      </p>
      {
        titleIcon && (
          <img className='dbd-realtime-icon-img' src={getImg(formatConfig.titleIcon)} />
        )
      }
    </>
  ),[formatConfig, isEN]);

  const explainCom = () => {
    if(explain){
      return (
        <div className='table-line-time dbd-realtime-revenue-title'>
          {explain}
          {
            redExplain && (
              <span className='table-line-explain-red'> {redExplain}</span>
            )
          }
        </div>
      );
    }
    return null;
  };

  const formatterValue = (value: string | number): string | number => {
    if (formatRule) {
      const formattedValue = dataFormat({value, formatRule});
      if(isMoney && value) {
        return `$${formattedValue}`;
      }
      return formattedValue;
    }
    
    if (getIsErrorValue(value)){
      return '-';
    }
    if(isMoney){
      return formatNum(value, true, false, formatConfig?.currencyUnit || '$');
    }
    return formatNum(value, false, true);
  };

  const getVal = (val: string, _type: string) => (!getIsErrorValue(val) && (_type === 'percent') ? formatPercentNum(parseFloat(val)) : formatterValue(val));

  const chartSetting = useMemo(() => {
    const seriesConfig = chart?.series;
    const tooltipConfig = chart?.tooltip;
    const type = chart?.type;
    const setting = {
      legendData: [],
      xAxisData: [],
      dataByLegend: [],
      series: [],
      tooltipLabel: '',
      height: chart?.height || 400,
      maxLength: 0
    };

    if(seriesConfig){

      const legendData = seriesConfig.map((v: { changeDateTitle: string; changeDateTitleCn: string; title: string; titleCn: string; formatTitle: string; defaultDateCheck:boolean; })=>
      {
        if(v.defaultDateCheck && isReleaseDateChange){
          return isEN ? v.changeDateTitle : v.changeDateTitleCn;
        }
        if(isChangeDate && v.changeDateTitle && v.changeDateTitleCn && !v.defaultDateCheck){
          return isEN ? v.changeDateTitle : v.changeDateTitleCn;
        }
        return v.title && v.titleCn ? isEN ? v.title : v.titleCn : formatMessage({ id: v.formatTitle });
      });

      const dataByLegend = seriesConfig.map((v: { valueKey: string | number; })=>componentData?.[v.valueKey] || []);

      const maxArr = (JSON.parse(JSON.stringify(dataByLegend)).sort((a: string | any[], b: string | any[]) => (b.length - a.length)))[0];

      const xAxisData = maxArr.map((i: any) => {
        const time = i?.time_dot ? !i.time_dot.includes('-') ? splitDateTimeNoSpace(i?.time_dot) : i?.time_dot : '';
        return `${formValue?.singleDaily}${time.slice(10)}`;
      });

      const series = legendData.map((i: string, ind: number) => {
        const arr = dataByLegend[ind];
        const data = xAxisData.map((item: any, index: number) => arr?.[index] ? arr[index]?.count : '');
        const color = seriesConfig[ind]?.color;
        return {
          name: i,
          type,
          data,
          ...(color ? { color } : {}),
        };
      });

      setting.legendData = legendData;
      setting.dataByLegend = dataByLegend;
      setting.xAxisData = xAxisData;
      setting.series = series;
      setting.maxLength = maxArr.length;
    }

    if(tooltipConfig){
      const tooltipLabel = tooltipConfig.title && tooltipConfig.titleCn ? isEN ? dynamicString(tooltipConfig.title, data) : dynamicString(tooltipConfig.titleCn, data) : formatMessage({ id: tooltipConfig.formatTitle });
      setting.tooltipLabel = tooltipLabel;
    }
    return setting;
  },[chart, componentData, locale, JSON.stringify(formValue), data]);

  const isNodata = useMemo(() => {
    // 如果接口数据的值全部为空，则为空数据
    const nodataArr = chartSetting?.series?.map(item => item?.data).flat();
    let flag = nodataArr?.filter(item => item !== '')?.length === 0;
    if(noDataDisplay){
      flag =  nodataArr?.length === 0;
    }
    return flag || componentData?.length === 0;
  }, [componentData, chartSetting, locale, formValue]);

  const tableSetting = useMemo(() => {
    const tableOption = table?.option || [];
    const sort = table?.sort || true;
    const columns = table?.columns || [];
    const defaultValue = table?.defaultValue || [];

    const setting = {
      option: [],
      config: {
        isSortable: sort,
        columns,
        defaultValue
      },
      dataList: [],
      downloadDataList: [],
      height: table?.height || 400,
      isRealtime: table?.isRealtime || false,
      downloadOption: []
    };

    if(table){
      if(tableOption.length > 0){
        const option = tableOption.map((val: { changeDateTitle: string; changeDateTitleCn: string; title: string; titleCn: string; key: string; align: string; width: string; formatTitle: string; valueKey: string | number; defaultDateCheck:boolean; })=> {
          if(val.defaultDateCheck && isReleaseDateChange){
            val.title =  val.changeDateTitle;
            val.titleCn = val.changeDateTitleCn;
          }
          if(isChangeDate && val.changeDateTitle && val.changeDateTitleCn && !val.defaultDateCheck){
            val.title =  val.changeDateTitle;
            val.titleCn = val.changeDateTitleCn;
          }
          return {
            key: val.key || '',
            align: val.align || 'left',
            width: val.width || 100,
            header: val.title && val.titleCn ? isEN ? val.title : val.titleCn : formatMessage({id: val.formatTitle}) || '',
            render: (cvm: any) => (<span>{cvm?.[val.valueKey]}</span>)
          };
        });

        const downloadOption = tableOption.map((val: { valueKey: string; title: string; titleCn: string; formatTitle: string; type: string; format: string; })=> ({
          key: val.valueKey || '',
          header: val.title && val.titleCn ? isEN ? val.title: val.titleCn: formatMessage({id: val.formatTitle}) || '',
          type: val.type || '',
          format: val.format || ''
        }));

        const dataByLegend = tableOption.map((v: { key: string | number; })=>componentData?.[v.key] || []);

        const maxArr = (JSON.parse(JSON.stringify(dataByLegend)).sort((a: string | any[], b: string | any[]) => (b.length - a.length)))[0];

        const xAxisData = maxArr.map(i => {
          const time = i?.time_dot ? !i.time_dot.includes('-') ? splitDateTimeNoSpace(i?.time_dot) : i?.time_dot : '-';
          return `${formValue?.singleDaily}${time.slice(10)}`;
        });

        const dataList = [];
        const downloadDataList = [];
        for (let i = 0;i < xAxisData.length;i++) {
          const obj : any = {};
          const downloadObj : any = {};
          const time = xAxisData[i];
          for (let j = 0;j < tableOption.length;j++) {
            const {valueKey} = tableOption[j];
            if(valueKey === 'time'){
              obj[valueKey] =  time?.slice(11, 16);
              downloadObj[valueKey] =  time?.slice(11, 16);
            }else{
              obj[valueKey] = formatRule ? dataFormat({ value: getCellValue(dataByLegend, j, i), formatRule }) : isPercent ? formatPercentNum(getCellValue(dataByLegend, j, i), 2, false) : formatNum(getCellValue(dataByLegend, j, i), false, isNoMoney);
              downloadObj[valueKey] = formatRule ? dataFormat({ value: getCellValue(dataByLegend, j, i), formatRule }) : isPercent ? formatPercentNum(getCellValue(dataByLegend, j, i), 2, false) : getCellValue(dataByLegend, j, i);
            }
          }
          downloadDataList.push(downloadObj);
          dataList.push(obj);
        }

        setting.dataList = dataList;
        setting.downloadDataList = downloadDataList;
        setting.option = option;
        setting.downloadOption = downloadOption;
      }
    }
    return setting;
  },[table, componentData, locale, JSON.stringify(formValue)]);

  const downloadSetting = useMemo(()=> {
    interface Setting {
      fileName: string;
      options?: {
        timeScale?: any[]; // 或者根据实际情况定义更具体的类型
        default?: string;
        sort?: number;
        sorting?: boolean;
      };
      title: string;
    }

    const setting: Setting= {
      fileName: '',
      options: {},
      title: ''
    };

    if(download && Object.keys(download).length !== 0){
      setting.fileName = download.fileName && download.fileNameCn ? isEN ? dynamicString(download.fileName, data) : dynamicString(download.fileNameCn, data) : download?.formatFileName ? formatMessage({ id: download?.formatFileName }) : '';
      setting.title = download.title && download.titleCn ? isEN ? download.title : download.titleCn : download?.formatTitle ? formatMessage({ id: download?.formatTitle }) : '';
      setting.options = {
        timeScale: [],
        default: '5',
        sort: download?.sort === 'asc' ? 0 : 1,
        sorting: download?.sorting || false,
      };

      // Less than 1 hour
      const l1h = tableSetting?.dataList?.length ? moment(tableSetting?.dataList?.[tableSetting?.dataList?.length - 1].time, 'HH:mm').hour() < 1 : false;
      setting.options.timeScale = download?.type?.map((val: { name: string; title: string; titleCn: string; formatTitle: string; disabled: string; beforeDownload: any; })=> ({
        name: val.name,
        label: val.title && val.titleCn ? isEN ? val.title : val.titleCn : val?.formatTitle ? formatMessage({ id: val?.formatTitle || '' }) : '',
        disabled: val.disabled === 'l1h' ? l1h : false,
        beforeDownload: val.beforeDownload || ''
      }));
      setting.options.default = download?.default || '5';
    }
    return setting;
  },[download, locale, tableSetting, data]);

  const renderList = useMemo(() => [
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: "common_line_chart" }),
      component:
        <LineChart
          ripples
          height={chartSetting?.height}
          hasDataZoom={true}
          legendData={chartSetting?.xAxisData.length > 0 ? chartSetting?.legendData : []}
          xAxisData={chartSetting?.xAxisData || []}
          series={chartSetting?.series || []}
          notMerge={false}
          formatterFunc={(e) => (lineFormatterFunc(e, chartSetting?.tooltipLabel, isPercent, isMoney || ["thousands2Decimal"]?.includes(formatRule)))}
          setOtherConfigFunc={(opt) => {
            opt.grid.x2 = 15;
            opt = addChartSymbol(opt, {
              showList: ['new', 'max'],
              formatter: (params: any) => {
                if (formatRule) {
                  const formattedValue = dataFormat({value: params?.value, formatRule});
                  if(isMoney && params?.value) {
                    return `$${formattedValue}`;
                  }
                  return formattedValue;
                }
                if(isPercent){
                  return formatPercentNum(params?.value, 2, false);
                }
                return formatNum(params?.value, false, isNoMoney);
              },
              todayLength: chartSetting?.maxLength || 0
            });
            // opt= addNewRadarSymbol(opt);
            opt = DataZoom(opt);
            opt = contrastLine(opt, {
              formatter: (value: any) => value ? value?.slice(11, 16) : ''
            });
            opt.yAxis.axisLabel.formatter = (value: any) => yAxisFormat(value, isPercent, 0);
            opt = xAxisHighLight(opt, {
              formatter: (params: any) => params?.value ? params?.value?.slice(11, 16) : '',
              isRealtime: true
            });
            return opt;
          }}
          chartEventConfig={ event && event.show === true ? slotConfigFunc : undefined}
        />
      ,
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: "common_table" }),
      component: <MiniTable
        isRealTime
        loading={loading}
        tableOption={tableSetting.option || []}
        tableConfig={tableSetting.config}
        tableList={tableSetting.dataList || []}
        height={tableSetting.height} />,
    },
  ], [chartSetting, tableSetting, slotConfigFunc]);

  const downloadClick = () => {
    if (download && Object.keys(download).length !== 0) {
      setShowDownload(true);
    }
    else{
      downloadData(downloadOptions);
    }
  };

  const downloadData = (downloadOptions: { sort: number; timeScale: any; }) => {
    const {sort} = downloadOptions;
    let filterName = downloadSetting.fileName || title || '';
    // 表头
    interface TableHeadItem {
      key: string;
      header: string;
      type?: string;
      format?: string;
    }

    const tableHead : TableHeadItem[] = tableSetting?.downloadOption.map((val : TableHeadItem)=>({
      key: val.key,
      header: val.header,
      type: val.type,
      format: val.format
    }));

    // Download Only Hour
    const hourDownload = (data: any[]) => data.filter((item: { time: string; }) => {
      const min: number = moment(item.time, 'HH:mm').minute();
      return min === 0 || (item.time === '23:59');
    });

    const tableData : any[] = tableSetting?.downloadDataList || [];
    const timeScaleObject = downloadSetting?.options?.timeScale && downloadSetting?.options?.timeScale.length > 0 ? downloadSetting?.options?.timeScale.find(item => item.name === downloadOptions.timeScale) : {};
    const beforeDownload = timeScaleObject.beforeDownload || '';

    let filterData = _.cloneDeep(tableData);
    if(beforeDownload === 'hourDownload'){
      filterData = hourDownload(tableData);
      // 60分钟时间粒度名称特殊处理
      filterName = isEN ? filterName?.replace('5 min', '1 hour') : filterName?.replace('5 分钟', '1 小时');
    }
    if (sort === 1) {
      filterData.reverse();
    }
    exportExcel(filterName, filterData, tableHead);
  };

  const subTitleList = useMemo(() => {
    const title = subTitle || [];
    return title.map((val: { title: string; titleCn: string; formatTitle: string; valueKey: string | number; unit: string; color: string; bold: boolean; formatRule?: string; dataFormat?: string; }) => ({
      title: val.title && val.titleCn ? isEN ? dynamicString(val.title, data) : dynamicString(val.titleCn, data) : formatMessage({ id: val.formatTitle }),
      value: loading ? '-' : componentData?.[val.valueKey] || '-',
      unit: val?.unit || '',
      // eslint-disable-next-line no-unneeded-ternary
      color: val?.color,
      bold: val?.bold !== false,
      formatRule: val?.formatRule,
    }));
  },[subTitle, componentData, loading, isEN]);

  const subTitleComInfo = (data: any, index: number) => {
    const { title, value, unit, bold, color, formatRule} = data || {};
    const correctVal = !getIsErrorValue(value);
    const val = !(Number(value) < 0);
    
    // 如果有 formatRule 或 dataFormat,使用它们来格式化值
    let formattedValue = value;
    if (correctVal && (formatRule)) {
      if (formatRule) {
        formattedValue = dataFormat({ value, formatRule: dynamicString(formatRule, data) });
      }
    }
    
    const resultObj = {
      date: correctVal ? value : '-',
      percent: correctVal ? formatPercentNum(parseFloat(value), 1, true) : '-',
      number: correctVal ? getVal(parseFloat(value), '') : '-'
    };
    
    // 如果有 formatRule 或 dataFormat,直接使用格式化后的值
    const displayValue = formatRule && correctVal ? formattedValue : resultObj?.[unit as keyof typeof resultObj];
    const isHaveIcon = unit !== 'date' && unit !== 'number';
    
    return (
      <div className={cx(
        { "table-line-text": true },
        { 'table-line-text-normal': !bold },
      )} key={`itemPercent_${index}`}>
        {title}{!isHaveIcon && ':'}&nbsp;&nbsp;
        <Text className={cx(
          { "section-data-val": true },
          { 'table-line-text-normal': !bold },
          !color && {
            'text-green': correctVal && val,
            'text-red': correctVal && !val,
            'text-gray': Number(value) === 0,
          },
        )} style={ color ? { color } : undefined }>
          {displayValue}
          {
            isHaveIcon && (correctVal && ((Number(value) === 0)
              ? <Icon type={"trend-zero"} />
              : (Number(value) > 0)
                ?  <SvgIcon className='overlay-card-body-icon' type="green-up-new" />
                :  <SvgIcon className='overlay-card-body-icon' type="red-down-new" />))
          }
        </Text>
      </div>
    );
  };

  // 折线图模块上方info信息
  const subTitleCom = () => (
    <>
      {
        explain && explainCom()
      }
      {
        !loading && !isNodata && (
          <div className='table-line-flex'>
            <div className={cx(
              { "table-line-num": true },
              { 'table-line-num-gray': getVal(count, '') === '-' },
            )}>{isNodata ? '-' : getVal(count, config?.dataType)}</div>
            {
              subTitleList.map((i: any, index: number) => subTitleComInfo(i, index))
            }
          </div>
        )
      }
    </>
  );

  return (
    <div className='dbd-realtime-users-user-tag'>
      {
        <CardBox
          className={className || ''}
          titleCom={titleCom}
          bordered={bordered}
          height={height|| 430}
          // key={JSON.stringify(formValue) + JSON.stringify(title)}
          subTitleCom={subTitleCom()}
          renderList={renderList}
          downloadClick={downloadClick}
          isLoading={loading}
          isError={error}
          isNodata={isNodata}
          failedMsg={formatMessage({ id: 'common_loading_failed_realtime'})}
        />
      }

      {
        event?.show === true && (
          <EventDetailsPop
            currentEvents={currentEvents}
            pathname={linkPath}
            timeType={event.timeType}
            popRef={popRef}
            style={event.cssStyle || {}}
          />
        )
      }

      {
        (downloadSetting.options && Object.keys(downloadSetting.options).length !== 0) && (
          <DownloadOptionsModalCo
            title={downloadSetting.title || ''}
            visible={showDownload}
            options={downloadSetting.options}
            onConfirm={(formValue)=> downloadData(formValue)}
            formatMessage={formatMessage}
            onExited={()=>setShowDownload(false)}
          />
        )
      }

    </div>
  );
};

export default RealtimeRetentionCo;

